import { useEffect, useMemo, useState } from 'react';
import { View, Switch } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { ThemedText, Caption, Body } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/theme';
import type { Booking, FlightNotificationPreferences, StatusUnavailableReason } from '@/services/bookings';
import {
  getStatusColor,
  getStatusLabel,
  formatDelay,
  isCheckInAvailable,
} from '@/services/flightStatus';
import { CheckInButton } from './CheckInButton';

/** Format a date for display; if time is midnight, show date only */
function formatDepartureArrival(d: Date | undefined): string | null {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  const isMidnight = date.getHours() === 0 && date.getMinutes() === 0;
  if (isMidnight) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isLocalMidnight(d: Date): boolean {
  return d.getHours() === 0 && d.getMinutes() === 0;
}

function localCalendarKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Use calendarAnchor's local Y-M-D with clock fields from clockSource (for same offset across days). */
function combineLocalCalendarWithClock(calendarAnchor: Date, clockSource: Date): Date {
  return new Date(
    calendarAnchor.getFullYear(),
    calendarAnchor.getMonth(),
    calendarAnchor.getDate(),
    clockSource.getHours(),
    clockSource.getMinutes(),
    clockSource.getSeconds(),
    clockSource.getMilliseconds(),
  );
}

/** Apply HH:mm (24h) itinerary time onto a calendar day in local time. */
export function withTimeOnDate(day: Date | undefined, time24: string | undefined): Date | undefined {
  if (!day || !time24 || time24 === 'flexible') return undefined;
  const parts = time24.trim().split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] || '0', 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return undefined;
  const d = new Date(day);
  d.setHours(h, m, 0, 0);
  return d;
}

function preferItineraryWhenApiIsDateOnly(
  api: Date | undefined,
  itinerary: Date | undefined
): Date | undefined {
  if (!api) return itinerary;
  if (!itinerary) return api;
  if (isLocalMidnight(api)) return itinerary;
  return api;
}

function reconcileDepartureArrival(
  dep: Date | undefined,
  arr: Date | undefined,
  hintDep: Date | undefined,
  hintArr: Date | undefined
): { dep: Date | undefined; arr: Date | undefined } {
  let d = dep;
  let a = arr;

  // When API returns correct clock but wrong calendar day (stale lookup), adopt the planner/itinerary day.
  if (hintDep && d && localCalendarKey(d) !== localCalendarKey(hintDep)) {
    const apiDep = d;
    const apiArr = a;
    d = combineLocalCalendarWithClock(hintDep, apiDep);
    if (apiArr && apiArr.getTime() >= apiDep.getTime()) {
      a = new Date(d.getTime() + (apiArr.getTime() - apiDep.getTime()));
    }
  }

  if (hintArr && a && localCalendarKey(a) !== localCalendarKey(hintArr)) {
    a = combineLocalCalendarWithClock(hintArr, a);
  }

  d = preferItineraryWhenApiIsDateOnly(d, hintDep);
  a = preferItineraryWhenApiIsDateOnly(a, hintArr);

  if (!d || !a || a.getTime() >= d.getTime()) {
    return { dep: d, arr: a };
  }

  if (hintDep && hintArr && hintArr.getTime() >= hintDep.getTime()) {
    return { dep: hintDep, arr: hintArr };
  }
  if (hintArr && hintArr.getTime() >= d.getTime()) {
    return { dep: d, arr: hintArr };
  }
  if (hintDep) {
    d = hintDep;
    if (a.getTime() < d.getTime()) {
      a = hintArr && hintArr.getTime() >= d.getTime() ? hintArr : undefined;
    }
  } else {
    a = hintArr ?? undefined;
  }
  return { dep: d, arr: a };
}

type FlightStatusCardProps = {
  booking: Booking;
  /** Itinerary-derived instants (e.g. from item start/end times) to disambiguate date-only API times. */
  itineraryDepartureAt?: Date;
  itineraryArrivalAt?: Date;
  onRefresh?: () => Promise<void> | void;
  isRefreshing?: boolean;
  onCheckIn?: () => void;
  isCheckInLoading?: boolean;
  onUpdatePreferences?: (prefs: FlightNotificationPreferences) => Promise<void>;
  isUpdatingPreferences?: boolean;
};

const preferenceDefaults: FlightNotificationPreferences = {
  delays: true,
  gateChanges: true,
  checkInReminders: true,
};

function getUnknownStatusMessage(
  reason: StatusUnavailableReason | undefined,
  departureDate: Date | undefined
): string {
  if (reason === 'future') {
    return 'Live status and gate info usually appear 24–48 hours before departure. Try refreshing then.';
  }
  if (reason === 'no_credentials') {
    return 'Status is not configured for this app. Check back later or use your airline’s website for live status.';
  }
  if (reason === 'api_error') {
    return 'We couldn’t reach the status provider. Pull to refresh in a moment or check the airline’s site.';
  }
  if (reason === 'no_data') {
    const isFuture = departureDate && new Date(departureDate) > new Date();
    return isFuture
      ? 'No schedule match for this date yet. Try again 24–48 hours before departure.'
      : 'No live data for this flight number on this date. Confirm times on your ticket or the airline’s site.';
  }
  if (departureDate && new Date(departureDate) > new Date()) {
    return 'Live status and gate info usually appear 24–48 hours before departure.';
  }
  return 'Live status isn’t available. You can still use airline check-in above.';
}

function UnknownStatusMessage({
  reason,
  departureDate,
  theme,
}: {
  reason: StatusUnavailableReason | undefined;
  departureDate: Date | undefined;
  theme: ReturnType<typeof useTheme>;
}) {
  const message = getUnknownStatusMessage(reason, departureDate);
  return (
    <Caption
      color={theme.colors.textSecondary}
      style={{ fontStyle: 'italic', marginTop: theme.spacing(-0.5) }}
    >
      {message}
    </Caption>
  );
}

const preferenceItems: Array<{
  key: keyof FlightNotificationPreferences;
  label: string;
  description: string;
}> = [
  {
    key: 'delays',
    label: 'Delay alerts',
    description: 'Notify me if this flight is delayed',
  },
  {
    key: 'gateChanges',
    label: 'Gate changes',
    description: 'Alert me when the gate changes near departure',
  },
  {
    key: 'checkInReminders',
    label: 'Check-in reminders',
    description: 'Remind me when online check-in opens',
  },
];

export function FlightStatusCard({
  booking,
  itineraryDepartureAt,
  itineraryArrivalAt,
  onRefresh,
  isRefreshing,
  onCheckIn,
  isCheckInLoading,
  onUpdatePreferences,
  isUpdatingPreferences,
}: FlightStatusCardProps) {
  const theme = useTheme();
  const status = booking.flightStatus;
  const statusLabel = getStatusLabel(status?.status);
  const statusColor = getStatusColor(status?.status);
  const delayText = formatDelay(status?.delayMinutes);
  const checkInAvailable = isCheckInAvailable(booking);

  const { dep: displayDeparture, arr: displayArrival } = useMemo(() => {
    const apiDep = status?.actualDeparture ?? status?.scheduledDeparture;
    const apiArr = status?.actualArrival ?? status?.scheduledArrival;
    return reconcileDepartureArrival(apiDep, apiArr, itineraryDepartureAt, itineraryArrivalAt);
  }, [
    status?.actualDeparture,
    status?.scheduledDeparture,
    status?.actualArrival,
    status?.scheduledArrival,
    itineraryDepartureAt,
    itineraryArrivalAt,
  ]);

  const staleCaption = useMemo(() => {
    if (!status?.lastUpdated && !status?.isStale && !status?.nextRefreshAt) return null;
    const parts: string[] = [];
    if (status?.isStale) {
      parts.push('Showing last known status');
    }
    if (status?.lastUpdated) {
      const lu = status.lastUpdated instanceof Date ? status.lastUpdated : new Date(status.lastUpdated);
      if (!Number.isNaN(lu.getTime())) {
        parts.push(`Updated ${lu.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
      }
    }
    if (status?.nextRefreshAt) {
      const nr = status.nextRefreshAt instanceof Date ? status.nextRefreshAt : new Date(status.nextRefreshAt);
      if (!Number.isNaN(nr.getTime()) && nr.getTime() > Date.now()) {
        parts.push(`Next refresh ~ ${nr.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`);
      }
    }
    return parts.length ? parts.join(' · ') : null;
  }, [status?.isStale, status?.lastUpdated, status?.nextRefreshAt]);
  const [preferences, setPreferences] = useState<FlightNotificationPreferences>(
    booking.notificationPreferences ?? preferenceDefaults
  );

  useEffect(() => {
    setPreferences(booking.notificationPreferences ?? preferenceDefaults);
  }, [booking.notificationPreferences]);

  const flightLabel = useMemo(() => {
    if (booking.airlineCode && booking.flightNumber) {
      return `${booking.airlineCode}${booking.flightNumber}`;
    }
    if (booking.provider) {
      return booking.provider;
    }
    return 'Flight';
  }, [booking.airlineCode, booking.flightNumber, booking.provider]);

  const handleTogglePreference = async (
    key: keyof FlightNotificationPreferences,
    value: boolean
  ) => {
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);

    if (!onUpdatePreferences) {
      return;
    }

    try {
      await onUpdatePreferences(updated);
    } catch (error) {
      console.error('Failed to update notification preferences', error);
      setPreferences(preferences);
    }
  };

  const renderInfoRow = (label: string, value?: string | null) => {
    if (!value) return null;

    return (
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Caption color={theme.colors.textSecondary}>{label}</Caption>
        <Body style={{ fontWeight: '600' }}>{value}</Body>
      </View>
    );
  };

  return (
    <Card variant="outlined" style={{ gap: theme.spacing(2) }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: theme.spacing(1) }}>
        <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
          <Caption color={theme.colors.textSecondary}>Flight Status</Caption>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
            <ThemedText variant="bodyStrong" color={statusColor} style={{ fontSize: 16 }}>
              {statusLabel}
            </ThemedText>
            {delayText && (
              <Caption color={theme.colors.warning}>
                {delayText} delay
              </Caption>
            )}
          </View>
          <Caption color={theme.colors.textSecondary}>{flightLabel}</Caption>
          {!!status?.provider && __DEV__ && (
            <Caption color={theme.colors.textSecondary} style={{ marginTop: 2 }}>
              Source: {status.provider}
            </Caption>
          )}
          {staleCaption && (
            <Caption color={theme.colors.textSecondary} style={{ marginTop: 2, fontStyle: 'italic' }}>
              {staleCaption}
            </Caption>
          )}
        </View>

        {onRefresh && (
          <Button
            label={isRefreshing ? 'Refreshing...' : 'Refresh'}
            onPress={onRefresh}
            disabled={isRefreshing}
            variant="secondary"
            icon={<Ionicons name="refresh" size={16} color={theme.colors.primary} />}
            style={{ alignSelf: 'flex-start', paddingHorizontal: theme.spacing(1.5) }}
          />
        )}
      </View>

      <View style={{ gap: theme.spacing(1.25), paddingVertical: theme.spacing(1) }}>
        {renderInfoRow('Route', booking.location)}
        {renderInfoRow(
          'Departure',
          formatDepartureArrival(displayDeparture) ??
            (booking.departureDate ? formatDepartureArrival(booking.departureDate) : null) ??
            (booking.departureDate ? booking.departureDate.toLocaleString() : null)
        )}
        {renderInfoRow(
          'Arrival',
          formatDepartureArrival(displayArrival) ??
            (booking.arrivalDate ? formatDepartureArrival(booking.arrivalDate) : null) ??
            (booking.arrivalDate ? booking.arrivalDate.toLocaleString() : null)
        )}
        {renderInfoRow('Gate', status?.gate ?? booking.gate)}
        {renderInfoRow('Terminal', status?.terminal ?? booking.terminal)}
        {(status?.gate || booking.gate || status?.terminal || booking.terminal) && (
          <Caption
            color={theme.colors.textSecondary}
            style={{ fontStyle: 'italic', marginTop: theme.spacing(-0.25), lineHeight: 18 }}
          >
            Gate and terminal can change. Confirm with your airline app or airport departure boards.
          </Caption>
        )}
      </View>
      {status?.status === 'unknown' && (
        <UnknownStatusMessage
          reason={status.statusUnavailableReason}
          departureDate={booking.departureDate}
          theme={theme}
        />
      )}

      <CheckInButton
        available={checkInAvailable}
        isLoading={isCheckInLoading}
        onPress={onCheckIn}
        checkInLink={booking.checkInLink}
      />

      <View style={{ gap: theme.spacing(1) }}>
        <Caption color={theme.colors.textSecondary}>Notification Preferences</Caption>
        {preferenceItems.map((item) => (
          <View
            key={item.key}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingVertical: theme.spacing(0.5),
            }}
          >
            <View style={{ flex: 1, marginRight: theme.spacing(1) }}>
              <Body style={{ fontWeight: '600' }}>{item.label}</Body>
              <Caption color={theme.colors.textSecondary}>{item.description}</Caption>
            </View>
            <Switch
              value={preferences[item.key]}
              onValueChange={(value) => handleTogglePreference(item.key, value)}
              disabled={isUpdatingPreferences}
            />
          </View>
        ))}
      </View>
    </Card>
  );
}

